import React from 'react';
import { useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  Flex,
  Heading,
  LoadingOrbit,
  Select,
  Text,
  Warning,
} from '@chatsdet/ui';
import { API_URL, MCP_URL } from '../../config.js';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../brand';

type StatePayload = {
  client_name?: string;
  scope?: string;
};

/** Decode a JWT payload without verifying. The server verifies on POST;
 *  this is only used to populate the consent UI. */
function decodeStatePayload(jwt: string | undefined): StatePayload {
  if (!jwt) return {};
  const parts = jwt.split('.');
  if (parts.length !== 3) return {};
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64 + '==='.slice((b64.length + 3) % 4))) as StatePayload;
  } catch {
    return {};
  }
}

export function ConnectMcp() {
  const { state } = useSearch({ from: '/connect/mcp' });
  const { user, organizations, isLoading } = useAuth();
  const name = BRAND.name;
  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);

  const payload = React.useMemo(() => decodeStatePayload(state), [state]);

  React.useEffect(() => {
    if (!selectedOrgId && user?.orgId) setSelectedOrgId(user.orgId);
  }, [selectedOrgId, user?.orgId]);

  React.useEffect(() => {
    if (!isLoading && !user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.replace(`/login?next=${next}`);
    }
  }, [isLoading, user]);

  const completeMutation = useMutation({
    mutationFn: async (decision: 'approve' | 'deny') => {
      if (!state) throw new Error('Missing state');

      // Approve: fetch a 60s consent JWT from apps/api (same-origin cookie
      // auth), hand it across to apps/mcp in the body. Deny: no auth needed
      // — server constructs the access_denied redirect from the state JWT.
      let consentToken: string | undefined;
      if (decision === 'approve') {
        const tokRes = await fetch(`${API_URL}/me/mcp-consent-token`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!tokRes.ok) {
          throw new Error(`Could not authenticate (status ${tokRes.status})`);
        }
        consentToken = ((await tokRes.json()) as { token: string }).token;
      }

      const body =
        decision === 'approve'
          ? { state, org_id: selectedOrgId, decision, consent_token: consentToken }
          : { state, decision };
      const res = await fetch(`${MCP_URL}/oauth/authorize/complete`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { error_description?: string };
        throw new Error(errBody.error_description ?? `OAuth complete failed: ${res.status}`);
      }
      return (await res.json()) as { redirect_to: string };
    },
    onSuccess: (data) => {
      if (data.redirect_to) window.location.href = data.redirect_to;
    },
  });

  if (isLoading || !user) {
    return (
      <Flex align="center" justify="center" className="min-h-screen">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  if (!state) {
    return (
      <Flex align="center" justify="center" className="min-h-screen bg-background">
        <Card padding="lg" className="max-w-md w-full mx-4">
          <CardContent>
            <Flex direction="column" align="center" gap="lg" className="text-center">
              <div className="p-3 bg-warning/10 rounded-full">
                <Warning size={48} className="text-warning" />
              </div>
              <Heading level={2}>Invalid connect request</Heading>
              <Text size="sm" className="text-muted-foreground">
                Missing or expired OAuth state. Restart the connect flow from your MCP client.
              </Text>
            </Flex>
          </CardContent>
        </Card>
      </Flex>
    );
  }

  const orgList = organizations ?? [];
  const clientName = payload.client_name ?? 'A third-party app';
  const scope = payload.scope ?? 'org:full';

  return (
    <Flex align="center" justify="center" className="min-h-screen bg-background">
      <Card padding="lg" className="max-w-md w-full mx-4">
        <CardContent>
          <Flex direction="column" gap="lg">
            <div>
              <Heading level={2}>Connect to {name}</Heading>
              <Text size="sm" className="text-muted-foreground mt-2">
                <strong>{clientName}</strong> is requesting access to your {name} organization with
                the scope <code>{scope}</code>. This will let it list workflows, run tests, read
                runs, and manage credentials on your behalf.
              </Text>
            </div>

            <div>
              <Text size="sm" weight="medium" className="mb-2">
                Organization to share
              </Text>
              {orgList.length <= 1 ? (
                <Text size="sm" className="text-muted-foreground">
                  {orgList[0]?.name ?? user.orgId}
                </Text>
              ) : (
                <Select
                  items={orgList.map((o) => ({ id: o.id, label: o.name }))}
                  selectedKey={selectedOrgId}
                  onSelectionChange={(key) => setSelectedOrgId(String(key))}
                />
              )}
            </div>

            {completeMutation.isError ? (
              <Text size="sm" className="text-destructive">
                {(completeMutation.error as Error).message}
              </Text>
            ) : null}

            <Flex gap="sm" justify="end">
              <Button
                variant="ghost"
                isDisabled={completeMutation.isPending}
                onPress={() => completeMutation.mutate('deny')}
              >
                Deny
              </Button>
              <Button
                variant="primary"
                isDisabled={!selectedOrgId || completeMutation.isPending}
                onPress={() => completeMutation.mutate('approve')}
              >
                {completeMutation.isPending ? 'Connecting…' : 'Approve'}
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>
    </Flex>
  );
}
